import { API_DASHBOARD } from './paths';
import type { ApiErrorPayload, MetaResponse, OrdersResponse, SummaryResponse } from './types';

/**
 * Thin fetch wrapper for the dashboard API.
 *
 * The browser never talks to MySQL — it only ever calls this API, which holds
 * the read-only credentials server-side. Errors arrive as a stable `{code,
 * message}` pair; raw SQL never reaches this layer.
 *
 * The base is derived from the build's mount prefix (see ./paths), never
 * hardcoded — an absolute `/api/...` would address the main domain instead.
 */
const BASE = API_DASHBOARD;

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      signal,
      // Sends the httpOnly session cookie; without this the API returns 401.
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError('NETWORK_ERROR', 'Could not reach the dashboard service. Check your connection and retry.', 0);
  }

  if (!response.ok) {
    let payload: ApiErrorPayload | null = null;
    try {
      payload = (await response.json()) as ApiErrorPayload;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      payload?.error.code ?? 'HTTP_ERROR',
      payload?.error.message ?? `Request failed (${response.status}).`,
      response.status,
    );
  }

  return (await response.json()) as T;
}

export interface OrderQuery {
  /** Inclusive first calendar day, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive last calendar day, `YYYY-MM-DD`. */
  to: string;
  statuses?: readonly string[];
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

function toParams(q: OrderQuery): URLSearchParams {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.statuses?.length) params.set('status', q.statuses.join(','));
  if (q.search?.trim()) params.set('q', q.search.trim());
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.sortBy) params.set('sortBy', q.sortBy);
  if (q.sortDir) params.set('sortDir', q.sortDir);
  return params;
}

export const api = {
  meta: (signal?: AbortSignal) => request<MetaResponse>('/meta', signal),

  summary: (from: string, to: string, signal?: AbortSignal) =>
    request<SummaryResponse>(`/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, signal),

  orders: (q: OrderQuery, signal?: AbortSignal) => request<OrdersResponse>(`/orders?${toParams(q)}`, signal),

  /**
   * Download URLs — both respect the active range, status filter and search.
   *
   * Excel is the default offered in the UI: CSV carries no type information, so
   * Excel mangles phone numbers and AWBs into scientific notation. The CSV is
   * still available for scripts and other tools.
   */
  exportUrl: (q: Omit<OrderQuery, 'page' | 'pageSize' | 'sortBy' | 'sortDir'>) =>
    `${BASE}/orders/export?${toParams(q)}`,

  exportXlsxUrl: (q: Omit<OrderQuery, 'page' | 'pageSize' | 'sortBy' | 'sortDir'>) =>
    `${BASE}/orders/export.xlsx?${toParams(q)}`,
};
